import { createStyles, makeStyles, useTheme } from "@material-ui/core";
import moment from "moment";
import React from "react";
import "chartjs-adapter-moment";
import {
  Chart as ChartJS,
  TimeScale, //Import timescale instead of category for X axis
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Watermark } from "@hirohe/react-watermark";

import { CustomTheme } from "../../../../../../types/customTheme";
import { getLocalStore } from "../../../../../../utils/store/localStore";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);
ChartJS.defaults.elements.line.tension = 0;
const useStyles = makeStyles<any>((theme: CustomTheme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.background.entity_background,
      border: `1px solid ${theme.palette.background.entity_border}`,
      paddingTop: "16px",
      borderRadius: "10px",
    },
    color: {
      color: theme.palette.custom.orange.contrastText,
    },
  }),
);

export interface ChartProps {
  data: any;
  dateDifference: any;
  dataSetLength: any;
  isEmpty?: any;
  currentStartDate: any;
  currentEndDate: any;
  order: any;
  isChannel: any;
  value: any;
}

/**
 * Chart Component:
 *
 * This component renders a line chart using Chart.js and React-Chartjs-2.
 * It displays data for a specified period and provides customization options for chart UI elements such as tooltips,
 * legends, and scales.
 */
const Chart: React.FunctionComponent<ChartProps> = ({
  data,
  dateDifference,
  dataSetLength,
  isEmpty,
  currentStartDate,
  currentEndDate,
  order,
  isChannel,
  value,
}) => {
  const classes = useStyles();
  const theme: CustomTheme = useTheme();

  /**
   * Function to determine the border color for a channel based on its name.
   * @param {string} channelName - The name of the channel.
   * @returns {string} The border color for the specified channel.
   */
  const handleChannelBorderColor = (channelName: string) => {
    return "#3A9D76";
  };

  /* Change the tooltip label using the x-axis of the chart. */
  const handleTooltipLabelDateFormat = (label: any) => {
    if (label === "12 AM") {
      return "12:00 AM";
    } else {
      // Input = 1 AM Output 1:00 AM
      return moment(label, "HH").format("hh:mm A");
    }
  };

  /* Change the chart UI after select date range */
  const optionsOneDateRange: any = {
    maintainAspectRatio: false,
    elements: {
      line: {
        // line width
        tension: 0,
        cubicInterpolationMode: "linear",
      },
      point: {
        radius: 2, // smaller dots
        hoverRadius: 4, // slightly larger on hover
        hitRadius: 5, // easier to interact with
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
    scales: {
      xAxes: {
        type: "time",
        time: {
          displayFormats: {
            day: "YYYY-MM-DD",
          },
          unit: "day",
        },
        ticks: {
          // Change the font size in the weekend dates.
          font: (c: any) => {
            return (
              (moment(c.tick.Date).day() === 6 ||
                moment(c.tick.Date).day() === 0) && { weight: "600" }
            );
          },
          // Change the font color in the weekend dates.
          color: (c: any) => {
            return moment(c.tick.Date).day() === 6 ||
              moment(c.tick.Date).day() === 0
              ? theme.palette.background.g_line_3
              : theme.palette.custom.orange.contrastText;
          },
          callback: function (label: any, index: any, labels: any) {
            labels[index].Date = label;
            return moment(label).format("Do");
          },
        },
        grid: {
          display: false,
        },
      },
      yAxes: {
        beginAtZero: true,
        suggestedMax: 100,
        ticks: {
          beginAtZero: false,
          max: 0,
          min: -0.5,
          color: theme.palette.custom.orange.contrastText,
        },
        grid: {
          display: true,
        },
      },
    },
    plugins: {
      title: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        callbacks: {
          labelPointStyle: (context: any) => {
            // A line will show the current Period.
            if (context.dataset.label === "Current Period") {
              return {
                pointStyle: "line",
                rotation: 0,
                radius: 5,
              };
            } else {
              // A dotted line will show the current Period.
              return {
                rotation: 0,
              };
            }
          },
          title: (context: any) => {
            return "";
          },
          labelColor: (context: any) => {
            // Change the label color in the previous period
            if (dataSetLength > context.datasetIndex) {
              return {
                borderColor: handleChannelBorderColor(context.dataset.key),
                backgroundColor: handleChannelBorderColor(context.dataset.key),
                borderRadius: 6,
                border: `1px solid ${theme.palette.background.entity_border}`,
              };
            } else {
              // Change the label color in the current period
              return {
                borderRadius: 6,
                border: `1px solid ${theme.palette.background.entity_border}`,
              };
            }
          },
          textDirection: "ltr",
          // Change the tooltip description
          label: (context: any) => {
            // Change tool tip description from previous periods
            if (context.dataset.label === "Previous Period") {
              // To display the value without a decimal format for an order.
              if (order) {
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  moment(context.label)
                    .subtract(dateDifference, "d")
                    .format("ll") +
                  "   " +
                  context.formattedValue.replace(",", "")
                );
              } else {
                // To display the value with a decimal format for an other tooltips.
                let val: any = 0;
                if (context.dataset.key === " Cost-to-Spend Ratio") {
                  val =
                    context.formattedValue.replace(",", "") > 0
                      ? parseFloat(
                          context.formattedValue.replace(",", ""),
                        ).toFixed(1)
                      : 0;
                } else if (
                  context.dataset.key === " Total Spend" ||
                  context.dataset.key === " Total Reward Cost"
                ) {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(2);
                } else {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(0);
                }
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  moment(context.label)
                    .subtract(dateDifference, "d")
                    .format("ll") +
                  "   " +
                  val
                );
              }
            } else {
              // To display the value without a decimal format for an order.
              if (order) {
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  moment(context.label).format("ll") +
                  "   " +
                  context.formattedValue.replace(",", "")
                );
              } else {
                let val: any = 0;
                if (context.dataset.key === " Cost-to-Spend Ratio") {
                  val =
                    context.formattedValue.replace(",", "") > 0
                      ? parseFloat(
                          context.formattedValue.replace(",", ""),
                        ).toFixed(1)
                      : 0;
                } else if (
                  context.dataset.key === " Total Spend" ||
                  context.dataset.key === " Total Reward Cost"
                ) {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(2);
                } else {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(0);
                }
                // To display the value with a decimal format for an other tooltips.
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  moment(context.label).format("ll") +
                  "   " +
                  val
                );
              }
            }
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  /* Change the chart UI after select one date */
  const dateOptions: any = {
    maintainAspectRatio: false,
    elements: {
      line: {
        // line width
        tension: 0,
        cubicInterpolationMode: "linear",
      },
      point: {
        radius: 2, // smaller dots
        hoverRadius: 4, // slightly larger on hover
        hitRadius: 5, // easier to interact with
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
    scales: {
      xAxes: {
        ticks: {
          color: theme.palette.custom.orange.contrastText,
        },
        grid: {
          display: false,
        },
      },
      yAxes: {
        beginAtZero: true,
        suggestedMax: 100,
        ticks: {
          color: theme.palette.custom.orange.contrastText,
        },
        grid: {
          display: true,
        },
      },
    },
    plugins: {
      title: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        multiKeyBackground: theme.palette.background.g_line_1,
        callbacks: {
          labelPointStyle: (context: any) => {
            // A line will show the current Period.
            if (context.dataset.label === "Current Period") {
              return {
                pointStyle: "line",
                rotation: 0,
                radius: 5,
              };
            } else {
              // A dotted line will show the current Period.
              return {
                rotation: 0,
                // pointStyle: isChannel
                //   ? handleChannelBorderColorLighter(context.dataset.key)
                //   : handleChannelLighter(context.dataset.key),
              };
            }
          },
          title: (context: any) => {
            return "";
          },
          labelColor: (context: any) => {
            // Change the label color in the previous period
            if (dataSetLength > context.datasetIndex) {
              return {
                borderColor: handleChannelBorderColor(context.dataset.key),
                backgroundColor: handleChannelBorderColor(context.dataset.key),
                borderRadius: 6,
                border: `1px solid ${theme.palette.background.entity_border}`,
              };
            } else {
              // Change the label color in the current period
              return {
                // borderColor: handleChannelBorderColorLighter(
                //   context.dataset.key,
                // ),
                // backgroundColor: handleChannelBorderColorLighter(
                //   context.dataset.key,
                // ),
                borderRadius: 6,
                border: `1px solid ${theme.palette.background.entity_border}`,
              };
            }
          },
          textDirection: "ltr",
          // Change the tooltip description
          label: (context: any) => {
            // Change tool tip description from previous periods
            if (context.dataset.label === "Previous Period") {
              // To display the value without a decimal format for an order.
              if (order) {
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  handleTooltipLabelDateFormat(context.label) +
                  "   " +
                  context.formattedValue.replace(",", "")
                );
              } else {
                let val: any = "";
                if (context.dataset.key === " Cost-to-Spend Ratio") {
                  val =
                    context.formattedValue.replace(",", "") > 0
                      ? parseFloat(
                          context.formattedValue.replace(",", ""),
                        ).toFixed(1)
                      : 0;
                } else if (
                  context.dataset.key === " Total Spend" ||
                  context.dataset.key === " Total Reward Cost"
                ) {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(2);
                } else {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(0);
                }
                // To display the value with a decimal format for an other tooltips.
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  handleTooltipLabelDateFormat(context.label) +
                  "   " +
                  val
                );
              }
            } else {
              // To display the value without a decimal format for an order.
              if (order) {
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  handleTooltipLabelDateFormat(context.label) +
                  "   " +
                  context.formattedValue.replace(",", "")
                );
              } else {
                let val: any = "";
                if (context.dataset.key === " Cost-to-Spend Ratio") {
                  val =
                    context.formattedValue.replace(",", "") > 0
                      ? parseFloat(
                          context.formattedValue.replace(",", ""),
                        ).toFixed(1)
                      : 0;
                } else if (
                  context.dataset.key === " Total Spend" ||
                  context.dataset.key === " Total Reward Cost"
                ) {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(2);
                } else {
                  val = parseFloat(
                    context.formattedValue.replace(",", ""),
                  ).toFixed(0);
                }
                // To display the value with a decimal format for an other tooltips.
                return (
                  "  " +
                  context.dataset.key +
                  "   " +
                  handleTooltipLabelDateFormat(context.label) +
                  "   " +
                  val
                );
              }
            }
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };
  return (
    <>
      <div
        className={classes.root}
        style={{ height: "320px" }}
      >
        {isEmpty ? (
          <Watermark
            lineHeight="1.2rem"
            opacity={0.2}
            rotate={-45}
            show
            text={"Sample Data"}
            textColor={theme.palette.custom.orange.contrastText}
            textSize={24}
            gutter={50}
            wrapperElement="div"
          >
            {" "}
            <div style={{ height: "320px" }}>
              <Line
                style={{ margin: "0px 16px 0px 16px", height: "320px" }}
                options={optionsOneDateRange}
                data={data}
              ></Line>
            </div>
          </Watermark>
        ) : (
          <Line
            style={{ margin: "0px 16px 0px 16px", height: "320px" }}
            options={optionsOneDateRange}
            data={data}
          ></Line>
        )}
      </div>
    </>
  );
};

export default Chart;
